import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { mdiCheckCircle, mdiCircleOutline, mdiContentCopy } from "@mdi/js";
import Icon from "@mdi/react";
import { isAxiosError } from "axios";
import clsx from "clsx";

import Banner from "components/Banner";
import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import DatePicker from "components/DatePicker";
import Header from "components/Header";
import Input from "components/Input";
import LoadingSpinner from "components/LoadingSpinner";
import { routes } from "constants/routes";
import { getApiErrorMessage } from "helpers/apiError";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import type { AlvysConnection } from "models/Alvys";
import {
  getAlvysConnectionAPI,
  removeAlvysConnectionAPI,
  updateAlvysConnectionAPI
} from "services/api/alvys";
import SecretField from "./SecretField";
import {
  AlvysFormValues,
  changedValues,
  clientIdMissing,
  clientSecretMissing,
  connectionSummary,
  formValuesFrom,
  hasCredentials,
  isNewKey,
  keySummary,
  pacificToday,
  renewalNotice
} from "./helpers";
import styles from "./style.module.scss";

type SecretName = "clientSecret" | "webhookSecret";

const NOT_REPLACING: Record<SecretName, boolean> = { clientSecret: false, webhookSecret: false };

const READ_ONLY_SCOPES = ["invoice:read", "load:read", "trip:read", "carrier:read"];
const LOAD_ERROR = "We couldn't load your Alvys settings. Please try again.";
const SAVE_ERROR = "We couldn't save your Alvys settings. Please try again.";
const DISCONNECT_ERROR = "We couldn't disconnect Alvys. Please try again.";

export default function AlvysIntegration() {
  const [connection, setConnection] = useState<AlvysConnection | null>(null);
  const [values, setValues] = useState<AlvysFormValues | null>(null);
  const [replacing, setReplacing] = useState(NOT_REPLACING);
  const [isRenewing, setRenewing] = useState(false);
  const [formVersion, setFormVersion] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [isUnavailable, setUnavailable] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSaving, setSaving] = useState(false);
  const [isDisconnectOpen, setDisconnectOpen] = useState(false);
  const firstStepRef = useRef<HTMLLIElement>(null);

  const resetForm = (data: AlvysConnection) => {
    setConnection(data);
    setValues(formValuesFrom(data));
    setReplacing(NOT_REPLACING);
    setRenewing(false);
    setSaveError("");
    setFormVersion((version) => version + 1);
  };

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await getAlvysConnectionAPI();
      resetForm(data);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) setUnavailable(true);
      else setLoadError(getApiErrorMessage(error, LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (isUnavailable) return <Navigate to={routes.SETTINGS} replace />;

  const renderContent = () => {
    if (isLoading) return <LoadingSpinner isFetching />;

    if (!connection || !values) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p role="alert" className={styles.error}>
            {loadError || LOAD_ERROR}
          </p>
          <Button variant="outline" onClick={load}>
            Try again
          </Button>
        </div>
      );
    }

    const update = changedValues(connection, values);
    const isDirty = Object.keys(update).length > 0;
    const missingClientId = clientIdMissing(connection, values);
    const missingClientSecret = clientSecretMissing(connection, values);
    const notice = renewalNotice(connection);
    const status = connectionSummary(connection);
    const isEnteringNewKey = isRenewing || replacing.clientSecret || isNewKey(connection, values);
    const dateField = isEnteringNewKey ? "newKeyExpiresOn" : "keyExpiresOn";

    const setValue = (name: keyof AlvysFormValues) => (value: string) =>
      setValues((current) => (current ? { ...current, [name]: value } : current));

    const replace = (name: SecretName) => (isReplacing: boolean) => {
      setReplacing((current) => ({ ...current, [name]: isReplacing }));
      if (!isReplacing) setValue(name)("");
    };

    const startRenewal = () => {
      setRenewing(true);
      setReplacing((current) => ({ ...current, clientSecret: true }));
      firstStepRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    };

    const cancelRenewal = () => {
      const stored = formValuesFrom(connection);
      setRenewing(false);
      setReplacing((current) => ({ ...current, clientSecret: false }));
      setValues((current) =>
        current
          ? {
              ...current,
              clientId: stored.clientId,
              clientSecret: "",
              keyExpiresOn: stored.keyExpiresOn,
              newKeyExpiresOn: ""
            }
          : current
      );
      setFormVersion((version) => version + 1);
    };

    const copyWebhookUrl = async () => {
      try {
        await navigator.clipboard.writeText(connection.webhook_url);
        showSuccessMessage("Webhook URL copied");
      } catch {
        showErrorMessage("Couldn't copy the webhook URL. Select it and copy it instead.");
      }
    };

    const handleSave = async () => {
      setSaving(true);
      setSaveError("");
      try {
        const { data } = await updateAlvysConnectionAPI(update);
        resetForm(data);
        showSuccessMessage("Alvys settings saved");
      } catch (error) {
        const message = getApiErrorMessage(error, SAVE_ERROR);
        setSaveError(message);
        showErrorMessage(message);
      } finally {
        setSaving(false);
      }
    };

    const handleDisconnect = async () => {
      setSaving(true);
      try {
        const { data } = await removeAlvysConnectionAPI();
        resetForm(data);
        showSuccessMessage("Alvys disconnected");
        setDisconnectOpen(false);
      } catch (error) {
        showErrorMessage(getApiErrorMessage(error, DISCONNECT_ERROR));
      } finally {
        setSaving(false);
      }
    };

    return (
      <>
        {notice && (
          <Banner
            className="desktop:flex-row desktop:items-center desktop:justify-between desktop:space-x-4 desktop:space-y-0"
            variant={notice.variant}
            title={notice.title}
            message={notice.message}
            actions={
              !isRenewing && (
                <Button size="sm" onClick={startRenewal}>
                  Renew key
                </Button>
              )
            }
          />
        )}

        <section className={styles.status}>
          <div>
            <span className={styles.statusLabel}>Connection</span>
            <span className={styles.statusValue}>
              <Icon
                path={status.isConnected ? mdiCheckCircle : mdiCircleOutline}
                className={clsx("size-5", status.isConnected ? "text-success" : "text-placeholder")}
              />
              {status.label}
            </span>
            <span className={styles.statusDetail}>{status.detail}</span>
          </div>
          <div>
            <span className={styles.statusLabel}>API key</span>
            <span className={styles.statusValue}>{keySummary(connection)}</span>
          </div>
        </section>

        <p className={styles.intro}>
          Connect Alvys so the loads you invoice in Alvys come to HaulPay as funding requests, with
          their documents.
        </p>

        <ol className={styles.steps}>
          <li ref={firstStepRef} className={styles.step}>
            <h3 className={styles.stepTitle}>
              <span className={styles.stepNumber}>1</span>Create a read-only API key in Alvys
            </h3>
            <p className={styles.stepText}>
              In Alvys, go to <b>Admin → API Access → Create New Application</b>. Give it read-only
              permissions:{" "}
              {READ_ONLY_SCOPES.map((scope, index) => (
                <span key={scope}>
                  <code>{scope}</code>
                  {index < READ_ONLY_SCOPES.length - 1 ? ", " : "."}
                </span>
              ))}{" "}
              Note the expiration date you set.
            </p>
          </li>

          <li className={styles.step}>
            <h3 className={styles.stepTitle}>
              <span className={styles.stepNumber}>2</span>Paste the key here
            </h3>
            {isRenewing && (
              <div className={styles.renewNote}>
                <span>
                  Create a new key in Alvys (step 1), then paste its Client ID, Client Secret and
                  expiry date here. Your current key keeps working until it expires, and the webhook
                  stays as it is.
                </span>
                <Button variant="text" size="sm" className="self-start" onClick={cancelRenewal}>
                  Cancel renewal
                </Button>
              </div>
            )}
            <Input
              id="alvys-client-id"
              label="Client ID"
              value={values.clientId}
              onChange={(event) => setValue("clientId")(event.target.value)}
              error={
                missingClientId
                  ? { type: "required", message: "Enter the key's Client ID." }
                  : undefined
              }
            />
            <SecretField
              id="alvys-client-secret"
              label="Client Secret"
              secret={connection.client_secret}
              value={values.clientSecret}
              isReplacing={replacing.clientSecret}
              onReplace={replace("clientSecret")}
              onChange={setValue("clientSecret")}
              error={
                missingClientSecret
                  ? "Enter the client secret that goes with this Client ID."
                  : undefined
              }
            />
            <DatePicker
              key={`${formVersion}-${dateField}`}
              label="Key expiry date"
              placeholder="MM/DD/YYYY"
              value={values[dateField]}
              onChange={setValue(dateField)}
              minimumDate={pacificToday()}
              clearable
            />
            {isEnteringNewKey && (
              <p className={styles.stepText}>Enter the date this new key expires in Alvys.</p>
            )}
          </li>

          <li className={styles.step}>
            <h3 className={styles.stepTitle}>
              <span className={styles.stepNumber}>3</span>Add the HaulPay webhook in Alvys
            </h3>
            <p className={styles.stepText}>
              In Alvys, go to <b>Settings → Connections → Webhooks</b>. Create a webhook with this
              URL and these events:
            </p>
            <div className={styles.inlineField}>
              <Input
                id="alvys-webhook-url"
                label="Webhook URL"
                value={connection.webhook_url}
                className="flex-1"
                readOnly
              />
              <Button
                variant="outline"
                className="gap-1.5"
                prefixNode={<Icon path={mdiContentCopy} className="size-5" />}
                onClick={copyWebhookUrl}>
                Copy
              </Button>
            </div>
            <ul className={styles.events}>
              {connection.webhook_events.map((event) => (
                <li key={event}>{event}</li>
              ))}
            </ul>
          </li>

          <li className={styles.step}>
            <h3 className={styles.stepTitle}>
              <span className={styles.stepNumber}>4</span>Paste the webhook&apos;s signing secret
            </h3>
            <SecretField
              id="alvys-webhook-secret"
              label="Webhook signing secret"
              secret={connection.webhook_secret}
              value={values.webhookSecret}
              isReplacing={replacing.webhookSecret}
              onReplace={replace("webhookSecret")}
              onChange={setValue("webhookSecret")}
            />
            <p className={styles.stepText}>
              Then switch <b>Send Events</b> on in Alvys.
            </p>
          </li>
        </ol>

        {saveError && (
          <p role="alert" className={styles.error}>
            {saveError}
          </p>
        )}
        <div className={styles.actions}>
          {hasCredentials(connection) && (
            <Button variant="outline" disabled={isSaving} onClick={() => setDisconnectOpen(true)}>
              Disconnect
            </Button>
          )}
          <Button
            className="desktop:w-40"
            disabled={!isDirty || missingClientId || missingClientSecret}
            loading={isSaving}
            onClick={handleSave}>
            Save
          </Button>
        </div>

        <ConfirmationModal
          title="Disconnect Alvys?"
          content="This removes your Alvys API key and webhook signing secret from HaulPay. Loads stop syncing until you connect again."
          isConfirmModalOpen={isDisconnectOpen}
          toggleConfirmModal={() => setDisconnectOpen(false)}
          labelOKButton="Disconnect"
          onOK={handleDisconnect}
          isLoading={isSaving}
        />
      </>
    );
  };

  return (
    <MainLayout
      desktopTitle="Alvys"
      customHeader={<Header label="Alvys" headerIcon=" " isHideDesktop />}
      hideNavigation
      className={styles.alvysContainer}>
      {renderContent()}
    </MainLayout>
  );
}
